import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Search,
  KeyboardArrowDown,
} from '@mui/icons-material';

const SECTION_CHIPS = ['Faktury sprzedaży', 'Magazyny', 'Faktury zakupu', 'Inne wydatki', 'Wypłaty pracownicze', 'Rozrachunki', 'Operacje bankowe', 'Wyciągi i uzgodnienia', 'Eksport do Hermesu', 'Podatki i ZUS'];
const SECTION_SLUGS = ['sales', 'warehouses', 'purchases', 'other-expenses', 'employee-payouts', 'settlements', 'bank-operations', 'bank-statements', 'hermes-export', 'taxes-zus'];
const SECTION_COLORS = ['#B2DFDB', '#FFE0B2', '#FEEED5', '#FCE4EC', '#E8EAF6', '#E6F0FF', '#E6F5E6', '#FFF3E0', '#F3E5F5', '#DCEDC8'];
const SECTION_DISPLAY_ORDER = [0, 1, 2, 3, 4, 9, 5, 6, 7, 8];
const CURRENT_SECTION = 4;

const EmployeePayoutsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sectionMenuAnchorEl, setSectionMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const sectionChipRef = React.useRef<HTMLDivElement>(null);

  const [query, setQuery] = React.useState('');
  const [rows] = React.useState<any[]>([]);
  const [loading] = React.useState(false);

  const handleSectionChange = (idx: number) => {
    const slug = SECTION_SLUGS[idx] || 'purchases';
    navigate(`/accounting/${slug}`, { replace: true });
    setSectionMenuAnchorEl(null);
  };

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowDown') {
        const tag = (document.activeElement?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        if (sectionMenuAnchorEl) {
          setSectionMenuAnchorEl(null);
        } else if (sectionChipRef.current) {
          setSectionMenuAnchorEl(sectionChipRef.current);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sectionMenuAnchorEl]);

  const filtered = rows.filter(r => !query || JSON.stringify(r).toLowerCase().includes(query.toLowerCase()));

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek z chipem sekcji */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40, mt: 0, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
          <Typography
            component="h1"
            sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '20px', lineHeight: '24px', fontWeight: 400, color: '#020242', m: 0 }}
          >
            Księgowość
          </Typography>
          <Box ref={sectionChipRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={SECTION_CHIPS[CURRENT_SECTION]}
              size="small"
              variant="filled"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{
                cursor: 'pointer',
                bgcolor: SECTION_COLORS[CURRENT_SECTION],
                color: '#020242',
                borderRadius: '4px',
                minHeight: 28,
                height: 28,
                '& .MuiChip-label': {
                  px: 1, py: 0,
                  fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 400, lineHeight: '16px', color: '#020242'
                }
              }}
            />
            <IconButton
              size="small"
              onClick={(e) => setSectionMenuAnchorEl(e.currentTarget)}
              sx={{ p: 0.25, width: 24, height: 28, borderRadius: '4px', color: '#020242' }}
              aria-label="Wybierz sekcję"
            >
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={sectionMenuAnchorEl}
              open={Boolean(sectionMenuAnchorEl)}
              onClose={() => setSectionMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{ sx: { mt: 1.5, minWidth: 180 } }}
            >
              {SECTION_DISPLAY_ORDER.map((idx) => (
                <MenuItem
                  key={SECTION_CHIPS[idx]}
                  selected={CURRENT_SECTION === idx}
                  onClick={() => handleSectionChange(idx)}
                  sx={{ gap: 1, '&.Mui-selected': { bgcolor: `${SECTION_COLORS[idx]}40` } }}
                >
                  <Box sx={{ width: 12, height: 12, borderRadius: 1, bgcolor: SECTION_COLORS[idx], flexShrink: 0 }} />
                  {SECTION_CHIPS[idx]}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>
      </Box>

      {/* Tytuł sekcji */}
      <Box sx={{ mt: '28px', mb: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 18, color: '#23272E' }}>
          Wypłaty pracownicze
        </Typography>
      </Box>

      {/* Filtry */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Szukaj..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, background: '#fff', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      </Box>

      {/* Tabela */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 1px 6px 0 rgba(0,0,0,0.07)', overflow: 'auto', maxHeight: 'calc(100vh - 340px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Data wypłaty</TableCell>
              <TableCell sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Pracownik</TableCell>
              <TableCell sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Typ</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Kwota brutto</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Kwota netto</TableCell>
              <TableCell sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Projekt</TableCell>
              <TableCell sx={{ fontWeight: 700, background: '#F7F7F8', fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: '#B0B0B0', fontFamily: 'Roboto, sans-serif' }}>Brak wypłat</TableCell></TableRow>
            ) : filtered.map((row: any, idx: number) => (
              <TableRow key={row.id || idx} hover>
                <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.payDate || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.employeeName || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.type || '—'}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.grossAmount != null ? Number(row.grossAmount).toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) : '—'}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.netAmount != null ? Number(row.netAmount).toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' }) : '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.project || '—'}</TableCell>
                <TableCell sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13 }}>{row.status || '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EmployeePayoutsPage;
